'use client';

import { useState } from 'react';
import { trackLead } from '@/lib/analytics';

export function CvDownloadForm() {
  const [email, setEmail] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/cv-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), marketing }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.error || 'Noe gikk galt. Prøv igjen.');
        setStatus('error');
        return;
      }
      setStatus('success');
      trackLead('generate_lead', { email, content_name: 'cv', method: 'cv-bygger' });
    } catch {
      setErrorMsg('Noe gikk galt. Prøv igjen.');
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <div className="my-8 bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
        <div className="flex justify-center mb-3">
          <svg viewBox="0 0 48 48" fill="none" className="w-12 h-12">
            <circle cx="24" cy="24" r="20" fill="#d1fae5" stroke="#10b981" strokeWidth="1.5"/>
            <path d="M15 24 L21 30 L33 18" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 className="font-semibold text-stone-900 mb-1">CV-malen er sendt!</h3>
        <p className="text-sm text-stone-500">Sjekk innboksen din — vi har sendt <strong>CV-Mal-Profesjonell-2026.docx</strong> til <span className="font-medium">{email}</span>.</p>
        <p className="text-xs text-stone-400 mt-2">Ikke funnet? Sjekk søppelpost-mappen.</p>
      </div>
    );
  }

  return (
    <div className="my-8 bg-white border-2 border-sky-100 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" stroke="#0ea5e9" strokeWidth="1.8">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round"/>
            <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round"/>
            <line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round"/>
            <polyline points="10 9 9 9 8 9" strokeLinecap="round"/>
          </svg>
        </div>
        <div>
          <h3 className="font-semibold text-stone-900 text-sm">Last ned gratis CV-mal (Word)</h3>
          <p className="text-xs text-stone-400">Profesjonell mal klar til å fylle ut — sendes rett til e-posten din</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <input
            type="email"
            required
            placeholder="Din e-postadresse"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400 transition-colors"
          />
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer group">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={marketing}
              onChange={e => setMarketing(e.target.checked)}
              className="sr-only"
            />
            <div className={`w-4 h-4 rounded border-2 transition-colors flex items-center justify-center ${marketing ? 'bg-sky-500 border-sky-500' : 'bg-white border-stone-300 group-hover:border-stone-400'}`}>
              {marketing && (
                <svg viewBox="0 0 12 12" fill="none" className="w-2.5 h-2.5">
                  <path d="M2 6 L5 9 L10 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
          </div>
          <span className="text-xs text-stone-500 leading-relaxed">
            Ja, send meg tips om jobbsøking, CV og karriere fra CoachGuiden (valgfritt — du kan melde deg av når som helst)
          </span>
        </label>

        {status === 'error' && (
          <p className="text-xs text-red-500">{errorMsg}</p>
        )}

        <button
          type="submit"
          disabled={status === 'loading' || !email.trim()}
          className="w-full py-2.5 bg-sky-500 hover:bg-sky-600 disabled:bg-stone-200 disabled:text-stone-400 text-white text-sm font-medium rounded-xl transition-colors"
        >
          {status === 'loading' ? 'Sender...' : 'Send meg CV-malen gratis →'}
        </button>

        <p className="text-[11px] text-stone-400 text-center">
          Vi deler aldri e-posten din med tredjeparter.
        </p>
      </form>
    </div>
  );
}
